import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { LuCreditCard, LuCopy, LuCircleCheck, LuSearch, LuSave } from 'react-icons/lu'
import api from '../services/api'
import './MisCVUsPage.css'

const HOUR_OPTIONS = [
  { label: '15 min', minutes: 15 },
  { label: '1 hora', minutes: 60 },
  { label: '2 horas', minutes: 120 },
  { label: '4 horas', minutes: 240 },
  { label: '8 horas', minutes: 480 },
  { label: '12 horas', minutes: 720 },
  { label: '24 horas', minutes: 1440 }
]

function MisCVUsPage() {
  const { isPortalUser, roleReady, portalRole } = useAuth()
  const [accounts, setAccounts] = useState([])
  const [canEditLabels, setCanEditLabels] = useState(false)
  const [canAlerts, setCanAlerts] = useState(portalRole === 'administrador')
  const [alertBanks, setAlertBanks] = useState(['CREDENCE', 'SUIGENERIS'])
  const [thresholds, setThresholds] = useState({})
  const [intervals, setIntervals] = useState({})
  const [emails, setEmails] = useState([])
  const [emailDraft, setEmailDraft] = useState('')
  const [search, setSearch] = useState('')
  const [copiedField, setCopiedField] = useState(null)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!roleReady || !isPortalUser) return
    load()
  }, [roleReady, isPortalUser])

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const accountsRes = await api.get('/users/my-accounts')
      const data = accountsRes.data || {}
      setAccounts(data.accounts || [])
      setCanEditLabels(!!data.can_edit_labels)
      setCanAlerts(!!data.can_use_balance_alerts)
      setAlertBanks(data.balance_alert_banks || ['CREDENCE', 'SUIGENERIS'])
      if (data.can_use_balance_alerts) {
        const alerts = await api.get('/balance-alerts')
        const body = alerts.data || {}
        setEmails(body.emails || [])
        setThresholds(body.thresholds || {})
        setIntervals(body.intervals || {})
      }
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar las cuentas')
    } finally {
      setLoading(false)
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return accounts
    return accounts.filter((a) =>
      [a.cvu, a.account_number, a.alias, a.label].some((v) => String(v || '').toLowerCase().includes(q))
    )
  }, [accounts, search])

  const copyToClipboard = (text, fieldId) => {
    navigator.clipboard.writeText(text)
    setCopiedField(fieldId)
    setTimeout(() => setCopiedField(null), 1500)
  }

  const saveLabel = async (account, label) => {
    setError('')
    setMessage('')
    try {
      await api.put(`/users/my-accounts/${account.id}/label`, { label })
      setAccounts((prev) => prev.map((a) => a.id === account.id ? { ...a, label } : a))
      setMessage('Etiqueta guardada')
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar la etiqueta')
    }
  }

  const saveAlert = async (account, threshold, minutes) => {
    setError('')
    setMessage('')
    try {
      await api.put('/balance-alerts/threshold', {
        cvu: account.cvu,
        threshold: threshold === '' ? '' : Number(threshold),
        interval_minutes: Number(minutes) || 480
      })
      setThresholds((prev) => ({ ...prev, [account.cvu]: threshold === '' ? undefined : Number(threshold) }))
      setIntervals((prev) => ({ ...prev, [account.cvu]: Number(minutes) || 480 }))
      setMessage(threshold === '' ? 'Alerta desactivada' : 'Alerta guardada')
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar la alerta')
    }
  }

  const addEmail = () => {
    const value = emailDraft.trim()
    if (!value || emails.includes(value)) return
    setEmails((prev) => [...prev, value])
    setEmailDraft('')
  }

  const saveEmails = async () => {
    setError('')
    setMessage('')
    try {
      await api.put('/balance-alerts/email', { emails })
      setMessage('Emails guardados')
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron guardar los emails')
    }
  }

  const sendTest = async () => {
    setError('')
    setMessage('')
    try {
      await api.post('/balance-alerts/test-email')
      setMessage('Mail de prueba enviado')
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo enviar la prueba')
    }
  }

  if (roleReady && !isPortalUser) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <p className="empty-state-large">Esta sección es solo para usuarios del portal.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Mis CVUs</h1>
      </div>

      {error && <div className="cvu-banner error">{error}</div>}
      {message && <div className="cvu-banner ok">{message}</div>}

      <div className="cvu-toolbar">
        <div className="summary-card compact">
          <span className="summary-label">Total de cuentas</span>
          <span className="summary-value">{accounts.length}</span>
        </div>
        <div className="cvu-search">
          <LuSearch />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por etiqueta o CVU…"
          />
        </div>
      </div>

      {loading ? (
        <div className="empty-state">Cargando cuentas...</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state-card">
          <h2>{accounts.length === 0 ? 'No hay cuentas disponibles' : 'No encontramos cuentas con esa búsqueda'}</h2>
        </div>
      ) : (
        <div className="cvus-grid">
          {filtered.map((account) => (
            <AccountCard
              key={account.id || account.cvu}
              account={account}
              canEditLabels={canEditLabels}
              canAlerts={canAlerts && alertBanks.includes((account.bank_id || 'CREDENCE').toUpperCase())}
              threshold={thresholds[account.cvu]}
              interval={intervals[account.cvu] || 480}
              copiedField={copiedField}
              onCopy={copyToClipboard}
              onSaveLabel={saveLabel}
              onSaveAlert={saveAlert}
            />
          ))}
        </div>
      )}

      {canAlerts && (
        <div className="alert-emails">
          <h3>Alerta por saldo mínimo</h3>
          <p>Avisamos al bajar del piso y, si sigue abajo, otra vez cada el intervalo elegido. Si el saldo sube y vuelve a bajar, avisamos de nuevo.</p>
          <div className="email-row">
            {emails.map((email) => (
              <button type="button" key={email} className="email-chip" onClick={() => setEmails(emails.filter((e) => e !== email))}>
                {email} ×
              </button>
            ))}
            <input
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail() } }}
              placeholder="Otro email y Enter"
            />
          </div>
          <div className="email-actions">
            <button type="button" className="btn-primary" onClick={saveEmails}><LuSave /> Guardar emails</button>
            <button type="button" className="btn-secondary" onClick={sendTest}>Enviar prueba</button>
          </div>
        </div>
      )}
    </div>
  )
}

function AccountCard({ account, canEditLabels, canAlerts, threshold, interval, copiedField, onCopy, onSaveLabel, onSaveAlert }) {
  const [label, setLabel] = useState(account.label || '')
  const [floor, setFloor] = useState(threshold ?? '')
  const [every, setEvery] = useState(interval || 480)

  useEffect(() => { setLabel(account.label || '') }, [account.label])
  useEffect(() => { setFloor(threshold ?? '') }, [threshold])
  useEffect(() => { setEvery(interval || 480) }, [interval])

  const name = label || account.account_number
  const everyLabel = HOUR_OPTIONS.find((opt) => opt.minutes === Number(every))?.label || '8 horas'

  return (
    <article className="cvu-block">
      <div className="cvu-block-top">
        <div>
          <h2 className="account-name">{name}</h2>
          <div className="account-alias">
            <span>Alias</span>
            <strong>{account.alias || '—'}</strong>
          </div>
        </div>
        <div className="account-ids">
          <Field label="Cuenta" value={account.account_number} copied={copiedField === `acc-${account.id}`} onCopy={() => onCopy(String(account.account_number || ''), `acc-${account.id}`)} />
          <Field label="CVU" value={account.cvu} copied={copiedField === `cvu-${account.id}`} onCopy={() => onCopy(String(account.cvu || ''), `cvu-${account.id}`)} />
        </div>
        <div className="account-actions">
          {canEditLabels && (
            <div className="quiet-edit">
              <input aria-label={`Etiqueta de ${name}`} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Etiqueta" />
              <button type="button" onClick={() => onSaveLabel(account, label.trim())}>Editar etiqueta</button>
            </div>
          )}
        </div>
      </div>
      {canAlerts && (
        <div className="alert-summary">
          <LuCreditCard aria-hidden="true" />
          <p>Mínimo {floor === '' || floor == null ? 'sin piso' : `$ ${Number(floor).toLocaleString('es-AR')}`} · Cada {everyLabel}</p>
          <div className="alert-controls">
            <input aria-label="Monto mínimo" value={floor} onChange={(e) => setFloor(e.target.value)} placeholder="Mínimo" inputMode="decimal" />
            <select aria-label="Frecuencia" value={every} onChange={(e) => setEvery(Number(e.target.value))}>
              {HOUR_OPTIONS.map((opt) => (
                <option key={opt.minutes} value={opt.minutes}>{opt.label}</option>
              ))}
            </select>
            <button type="button" onClick={() => onSaveAlert(account, floor, every)}>Configurar alertas</button>
          </div>
        </div>
      )}
    </article>
  )
}

function Field({ label, value, copied, onCopy }) {
  return (
    <div className="cvu-field">
      <label>{label}</label>
      <div className="cvu-value-container">
        <span className="cvu-value">{value}</span>
        <button className="btn-copy" onClick={onCopy} type="button">
          {copied ? <LuCircleCheck className="copied" /> : <LuCopy />}
        </button>
      </div>
    </div>
  )
}

export default MisCVUsPage
